import { create } from 'zustand'
import { apiClient } from '@/api/client'
import { queryClient } from '@/shared/lib/queryClientSingleton'
import { queryKeys } from '@/api/queryKeys'

// Theme preference: 'light' | 'dark' | 'system'
type ThemePref = 'light' | 'dark' | 'system'

interface ThemeState {
  preference: ThemePref
  /** Resolved effective theme (always 'light' or 'dark') */
  resolved: 'light' | 'dark'
  setPreference: (pref: ThemePref) => void
  /** Called after preferences load from server — syncs without writing back. */
  syncFromServer: (pref: ThemePref) => void
}

const STORAGE_KEY = 'sf_theme'

function getStoredPref(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch { /* SSR / privacy mode */ }
  return 'light' // default to light — the existing look stays unchanged
}

function resolveTheme(pref: ThemePref): 'light' | 'dark' {
  if (pref === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return pref
}

/** Apply the resolved theme to <html> so Tailwind's `dark:` variants activate */
function applyToDOM(resolved: 'light' | 'dark') {
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
  // Also set color-scheme so browser chrome (scrollbars, etc.) matches
  root.style.colorScheme = resolved
  // Update meta theme-color for mobile browser chrome
  const meta = document.getElementById('meta-theme-color') as HTMLMetaElement | null
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? '#110e1a' : '#1e3a5f')
  }
}

const initialPref = getStoredPref()
const initialResolved = resolveTheme(initialPref)
applyToDOM(initialResolved)

export const useThemeStore = create<ThemeState>((set) => ({
  preference: initialPref,
  resolved: initialResolved,

  setPreference: (pref) => {
    // 1. Persist locally for first-paint on next load
    localStorage.setItem(STORAGE_KEY, pref)
    // 2. Apply to DOM immediately
    const resolved = resolveTheme(pref)
    applyToDOM(resolved)
    set({ preference: pref, resolved })
    /*
     * 3. Write the new value straight into the cached preferences payload.
     *
     * Not cosmetic — this is what stops the theme flipping back on its own.
     * `settings.preferences` is cached with a 5-minute staleTime and shared by
     * AppLayout, PreferencesSection and HomeRedirect, all of which read `.theme`
     * and hand it to syncFromServer. Without this write the cache still holds
     * the theme from login, so the next consumer to mount inside that window
     * re-applies the OLD theme over the one the user just picked — which is
     * precisely what "opening Application Preferences turned the lights on" was.
     *
     * Updater form, and a no-op when the entry is absent: seeding a lone
     * {theme} object here would masquerade as a full preferences payload and
     * make every other field read as unset.
     */
    queryClient.setQueryData(queryKeys.settings.preferences, (old) =>
      old ? { ...old, theme: pref } : old,
    )
    // 4. Sync to server for cross-device persistence (fire-and-forget)
    apiClient.put('/team-members/me/preferences', { theme: pref }).catch(() => {
      // Non-critical — the localStorage value is still correct for this session
    })
  },

  syncFromServer: (pref) => {
    // Called when preferences load from server; does NOT write back to server.
    localStorage.setItem(STORAGE_KEY, pref)
    const resolved = resolveTheme(pref)
    applyToDOM(resolved)
    set({ preference: pref, resolved })
  },
}))

// Listen for OS-level theme changes so 'system' stays in sync
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const state = useThemeStore.getState()
    if (state.preference === 'system') {
      const resolved = resolveTheme('system')
      applyToDOM(resolved)
      useThemeStore.setState({ resolved })
    }
  })
}
